import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fmtMoney, todayISO } from "@/lib/utils-format";

const INVOICE_TEMPLATE_URL = "https://customer-assets.emergentagent.com/job_service-platform-61/artifacts/os8lrh4j_INvoice%20V1.jpg";
const BRAND_ORANGE = [232, 92, 36];
const BRAND_GREEN = [74, 124, 60];
const CHARCOAL = [33, 37, 41];

async function loadImageBase64(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      try { resolve(canvas.toDataURL("image/jpeg")); }
      catch (e) { reject(e); }
    };
    img.onerror = reject;
    img.src = url;
  });
}

export async function generateInvoicePDF(doc) {
  const pdf = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  try {
    const bg = await loadImageBase64(INVOICE_TEMPLATE_URL);
    pdf.addImage(bg, "JPEG", 0, 0, pageW, pageH);
  } catch (err) {
    // brand template optional — falls back to plain PDF
    console.warn("Invoice template load failed; using plain PDF:", err);
  }

  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(...CHARCOAL);

  // Invoice meta (top-right area)
  pdf.setFontSize(11);
  pdf.text(String(doc.number || ""), pageW - 100, 192);
  pdf.text(String(doc.issue_date || ""), pageW - 100, 232);
  pdf.text(String(doc.service_date || doc.issue_date || ""), pageW - 100, 272);

  // Bill To
  pdf.setFontSize(11);
  let by = 360;
  pdf.text(String(doc.client_name || ""), pageW / 2 - 30, by);
  pdf.text(String(doc.address || ""), pageW / 2 - 30, by + 18);

  // Description / amount
  pdf.setFontSize(13);
  pdf.setFont("helvetica", "bold");
  pdf.text(String(doc.service || (doc.line_items?.[0]?.description || "")).toUpperCase(), 76, 620);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(11);
  pdf.text(fmtMoney(doc.subtotal || 0), pageW - 100, 760);
  pdf.text(fmtMoney(doc.tax || 0), pageW - 100, 786);
  pdf.text(fmtMoney(doc.deposit || 0), pageW - 100, 812);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.setTextColor(255,255,255);
  pdf.text(fmtMoney(doc.balance ?? doc.total ?? 0), pageW - 100, 840);

  // Payment method check
  if (doc.payment_method) {
    pdf.setTextColor(...BRAND_ORANGE);
    pdf.setFontSize(14);
    const map = { Cash: 235, Zelle: 320, check: 410, Check: 410, "Credit/Debit Card": 500, ACH: 500, "ACH Transfer": 500 };
    const x = map[doc.payment_method] || 235;
    pdf.text("X", x, 905);
  }

  // Paid stamp
  if (doc.status === "Paid") {
    pdf.setTextColor(74, 124, 60);
    pdf.setFontSize(48);
    pdf.setFont("helvetica", "bold");
    pdf.text("PAID", pageW / 2, pageH / 2, { align: "center", angle: -15 });
  }

  pdf.save(`Invoice-${doc.number || "doc"}.pdf`);
}

export function generateDocPDF({ kind, doc, business }) {
  if (kind === "INVOICE") {
    return generateInvoicePDF(doc);
  }
  const pdf = new jsPDF();
  const margin = 14;
  pdf.setFontSize(20);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(...BRAND_ORANGE);
  pdf.text("STAR CAPITAL LANDSCAPING", margin, 18);
  pdf.setTextColor(0,0,0);
  pdf.setFontSize(10);
  pdf.setFont("helvetica", "normal");
  pdf.text(business?.address || "Austin, TX", margin, 24);
  pdf.text("760-697-5023", margin, 29);
  pdf.text("starcapitallandscaping@gmail.com", margin, 34);

  pdf.setFontSize(24);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(...BRAND_ORANGE);
  pdf.text(kind, 200 - margin, 22, { align: "right" });
  pdf.setTextColor(0,0,0);
  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");
  pdf.text(`# ${doc.number || ""}`, 200 - margin, 30, { align: "right" });
  pdf.text(`Date: ${doc.issue_date || doc.valid_until || todayISO()}`, 200 - margin, 36, { align: "right" });

  pdf.setFont("helvetica", "bold");
  pdf.text("Bill To:", margin, 52);
  pdf.setFont("helvetica", "normal");
  pdf.text(doc.client_name || "", margin, 58);
  pdf.text(doc.address || "", margin, 64);

  const rows = (doc.line_items || []).map(li => [li.description, String(li.qty), fmtMoney(li.unit_price), fmtMoney(li.amount)]);
  autoTable(pdf, {
    startY: 75,
    head: [["Description", "Qty", "Unit Price", "Amount"]],
    body: rows.length ? rows : [[doc.service || "Service", "1", fmtMoney(doc.total||0), fmtMoney(doc.total||0)]],
    theme: "striped",
    headStyles: { fillColor: BRAND_ORANGE, textColor: 255, fontStyle: "bold" },
    styles: { fontSize: 10 },
  });

  const y = pdf.lastAutoTable.finalY + 10;
  pdf.setFontSize(10);
  pdf.text(`Subtotal: ${fmtMoney(doc.subtotal||0)}`, 200 - margin, y, { align: "right" });
  pdf.text(`Tax: ${fmtMoney(doc.tax||0)}`, 200 - margin, y + 6, { align: "right" });
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.text(`Total: ${fmtMoney(doc.total||0)}`, 200 - margin, y + 18, { align: "right" });

  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(9);
  pdf.text("Thank you for choosing Star Capital Landscaping!", 105, 280, { align: "center" });

  pdf.save(`${kind}-${doc.number || "doc"}.pdf`);
}
